import { Img, Reveal, ClipReveal, FloorLabel } from "../motion/Reveal";
import { Breadcrumbs } from "../layout/Breadcrumbs";
import { useDarkHero } from "../layout/HeaderTheme";
import { usePageMeta } from "../../hooks/useData";

const DarkHeroFlag = () => { useDarkHero(); return null; };

/**
 * variant: "image" (full-bleed dark) | "split" (text + image) | "text" (surface, text only)
 */
export const PageHero = ({
  variant = "text", crumbs = [], label, title, lead, image, imageAlt = "", actions, aside, meta, metaTitle, metaDescription,
  tall = false, testid = "page-hero", brand,
}) => {
  usePageMeta(metaTitle || title, metaDescription || lead);

  if (variant === "image") {
    return (
      <section data-testid={testid} data-variant="image" className={`relative flex ${tall ? "min-h-[100svh]" : "min-h-[82svh] md:min-h-[88svh]"} items-end overflow-hidden bg-navy text-white`}>
        <DarkHeroFlag />
        <div className="absolute inset-0">
          <Img src={image} alt={imageAlt} eager fetchPriority="high" sizes="100vw" className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(to_top_right,rgba(8,28,48,0.96)_0%,rgba(8,28,48,0.8)_35%,rgba(8,28,48,0.38)_65%,rgba(8,28,48,0.1)_100%)]" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-[linear-gradient(to_top,rgba(8,28,48,0.78)_0%,rgba(8,28,48,0)_100%)]" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-40 bg-[linear-gradient(to_bottom,rgba(8,28,48,0.55),rgba(8,28,48,0))]" aria-hidden="true" />
        <div className="absolute inset-x-0 top-[calc(var(--header-h)+28px)]">
          <div className="wrap"><Breadcrumbs items={crumbs} light /></div>
        </div>
        <div className="wrap relative w-full pt-48 pb-14 md:pb-20">
          <div className="grid-12 gap-y-10 items-end">
            <Reveal className={`col-span-12 ${aside ? "lg:col-span-8" : "lg:col-span-10 xl:col-span-9"}`}>
              {brand && <div className="mb-8" data-testid="page-brand">{brand}</div>}
              {label && <FloorLabel light>{label}</FloorLabel>}
              <h1 data-testid="page-title" className="mt-5 t-display max-w-[16ch]">{title}</h1>
              {lead && <p data-testid="page-lead" className="mt-8 max-w-[52ch] t-lead text-white/85">{lead}</p>}
              {actions && <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">{actions}</div>}
            </Reveal>
            {aside && <Reveal delay={150} className="col-span-12 lg:col-span-4">{aside}</Reveal>}
          </div>
          {meta && <Reveal delay={220} className="mt-14 md:mt-20 border-t border-white/15 pt-6">{meta}</Reveal>}
        </div>
      </section>
    );
  }

  if (variant === "split") {
    return (
      <section data-testid={testid} data-variant="split" className="bg-white pt-[calc(var(--header-h)+36px)] md:pt-[calc(var(--header-h)+56px)]">
        <div className="wrap">
          <Breadcrumbs items={crumbs} />
          <div className="mt-10 md:mt-16 grid-12 gap-y-12 items-end">
            <Reveal className="col-span-12 lg:col-span-6 lg:pb-2">
              {brand && <div className="mb-8" data-testid="page-brand">{brand}</div>}
              {label && <FloorLabel>{label}</FloorLabel>}
              <h1 data-testid="page-title" className="mt-5 t-h1 max-w-[14ch]">{title}</h1>
              {lead && <p data-testid="page-lead" className="mt-8 max-w-[46ch] t-lead text-muted">{lead}</p>}
              {actions && <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">{actions}</div>}
            </Reveal>
            <div className="col-span-12 lg:col-span-6">
              <ClipReveal className="aspect-[4/3] bg-surface">
                <Img src={image} alt={imageAlt} eager sizes="(min-width:1024px) 50vw, 100vw" className="h-full w-full object-cover" />
              </ClipReveal>
            </div>
          </div>
          {meta && <Reveal delay={200} className="mt-12 md:mt-16">{meta}</Reveal>}
        </div>
      </section>
    );
  }

  return (
    <section data-testid={testid} data-variant="text" className="bg-surface pt-[calc(var(--header-h)+36px)] md:pt-[calc(var(--header-h)+56px)] pb-16 md:pb-24">
      <div className="wrap">
        <Breadcrumbs items={crumbs} />
        <div className="mt-10 md:mt-16 grid-12 gap-y-12 items-end">
          <Reveal className={`col-span-12 ${aside ? "lg:col-span-7" : "lg:col-span-10"}`}>
            {brand && <div className="mb-8" data-testid="page-brand">{brand}</div>}
            {label && <FloorLabel>{label}</FloorLabel>}
            <h1 data-testid="page-title" className="mt-5 t-h1 max-w-[18ch]">{title}</h1>
            {lead && <p data-testid="page-lead" className="mt-8 max-w-[56ch] t-lead text-muted">{lead}</p>}
            {actions && <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">{actions}</div>}
          </Reveal>
          {aside && <Reveal delay={150} className="col-span-12 lg:col-span-4 lg:col-start-9">{aside}</Reveal>}
        </div>
        {meta && <Reveal delay={200} className="mt-12 md:mt-16">{meta}</Reveal>}
      </div>
    </section>
  );
};

export const PageSkeleton = () => (
  <div aria-busy="true" data-testid="page-loading">
    <div className="bg-surface pt-[calc(var(--header-h)+56px)] pb-24">
      <div className="wrap">
        <div className="h-3 w-40 animate-pulse bg-[rgba(8,28,48,0.08)]" />
        <div className="mt-16 h-3 w-24 animate-pulse bg-[rgba(8,28,48,0.08)]" />
        <div className="mt-6 h-14 w-3/4 max-w-3xl animate-pulse bg-[rgba(8,28,48,0.08)]" />
        <div className="mt-4 h-14 w-1/2 max-w-xl animate-pulse bg-[rgba(8,28,48,0.08)]" />
        <div className="mt-10 h-5 w-2/3 max-w-2xl animate-pulse bg-[rgba(8,28,48,0.06)]" />
      </div>
    </div>
  </div>
);
